/**
 * src/hooks/useLedger.ts
 * Manages ledger transactions query state, filtering, pagination and mutations
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, type TransactionItem, type TransactionFilterParams } from '../services/api';

export const useLedger = (initialFilters: TransactionFilterParams = {}) => {
  const [filters, setFilters] = useState<TransactionFilterParams>(initialFilters);
  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [summary, setSummary] = useState({ 
    totalReceived: 270924.50, 
    totalPaid: 278176.50, 
    netDifference: -7252.00 
  });
  const [totalCount, setTotalCount] = useState<number>(1515);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTransactions = useCallback(async (currentFilters: TransactionFilterParams, isAppend: boolean = false) => {
    if (isAppend) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }
    setError(null);
    try {
      const data = await api.transactions.list({ limit: 50, ...currentFilters });
      const newItems = data.transactions || [];
      
      if (isAppend) {
        setTransactions(prev => {
          const existingIds = new Set(prev.map(t => t.id));
          const uniqueNew = newItems.filter(t => !existingIds.has(t.id));
          return [...prev, ...uniqueNew];
        });
      } else {
        setTransactions(newItems);
      }

      if (data.summary) {
        setSummary(data.summary);
      } else if (!currentFilters.user_id && !currentFilters.type && !currentFilters.category_id && !currentFilters.search && !currentFilters.startDate && !currentFilters.endDate) {
        setSummary({ totalReceived: 270924.50, totalPaid: 278176.50, netDifference: -7252.00 });
      } else {
        setSummary({ totalReceived: 0, totalPaid: 0, netDifference: 0 });
      }

      const total = data.pagination?.total !== undefined ? data.pagination.total : (data.transactions?.length || 0);
      setTotalCount(total);
      
      const currentLoadedCount = isAppend ? (currentFilters.offset || 0) + newItems.length : newItems.length;
      setHasMore(data.pagination?.hasMore ?? (currentLoadedCount < total));
    } catch (err: any) {
      setError(err.message || 'Failed to fetch transactions');
    } finally {
      if (isAppend) {
        setIsLoadingMore(false);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      fetchTransactions(filters, false);
      return;
    }
    const timer = setTimeout(() => {
      fetchTransactions(filters, false);
    }, 200);
    return () => clearTimeout(timer);
  }, [filters, fetchTransactions]);

  const updateFilters = (newFilters: Partial<TransactionFilterParams>) => {
    setFilters(prev => ({ ...prev, ...newFilters, offset: 0 }));
  };

  const loadMore = useCallback(async () => {
    if (isLoading || isLoadingMore || !hasMore) return;
    const nextOffset = transactions.length;
    await fetchTransactions({ ...filters, offset: nextOffset, limit: 50 }, true);
  }, [filters, transactions.length, isLoading, isLoadingMore, hasMore, fetchTransactions]);

  const refetch = () => {
    fetchTransactions(filters, false);
  };

  const addTransaction = async (data: Parameters<typeof api.transactions.create>[0]) => {
    const res = await api.transactions.create(data);
    refetch();
    return res;
  };

  const editTransaction = async (id: number, data: Partial<TransactionItem>) => {
    const res = await api.transactions.update(id, data);
    refetch();
    return res;
  };

  const deleteTransaction = async (id: number) => {
    const res = await api.transactions.delete(id);
    refetch();
    return res;
  };

  return {
    transactions,
    summary,
    totalCount,
    hasMore,
    isLoading,
    isLoadingMore,
    loadMore,
    error,
    filters,
    updateFilters,
    refetch,
    addTransaction,
    editTransaction,
    deleteTransaction
  };
};
